import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React from "react";
import BulkOperationsModal from "../AnchorNew/BulkOperationModal";
import useFetch from "../Common/useFetch";
import ActionConfirmationModal from "./ActionConfirmationModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import EditInvoiceBulkModal from "./EditInvoiceBulkModal";

export default function BulkActionsDialog({
  open,
  onClose,
  selectedRowIds,
  getAllData,
  clearSelectedRows,
  useCase,
}: {
  open: boolean;
  onClose: () => void;
  selectedRowIds: string[];
  getAllData: () => void;
  clearSelectedRows: () => void;
  useCase: "dailyPayments" | "previousPayments";
}) {
  const [openEditInvoiceModal, setOpenEditInvoiceModal] = React.useState<boolean>(false);
  const [openMoveInvoiceModal, setOpenMoveInvoiceModal] = React.useState<boolean>(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = React.useState<boolean>(false);
  const [openBulkOperationsModal, setOpenBulkOperationsModal] = React.useState<boolean>(false);

  const handleMoveInvoices = async () => {
    await useFetch("/invoices/early-payment/2", "PUT", {
      data: { ids: selectedRowIds },
      failureMessage: "Failed to move invoices",
      showSuccessToast: true,
      thenCallBack: () => {
        getAllData();
        clearSelectedRows();
        onClose();
      },
      errorCallback: (error) => {
        console.error("Error moving invoices:", error);
      },
    });
  };

  const handleDeleteInvoices = async () => {
    const url = useCase === "dailyPayments" ? "/invoices/early-payment" : "/invoices/previous-payment";
    await useFetch(url, "DELETE", {
      config: {
        data: { ids: selectedRowIds },
      },
      failureMessage: "Failed to delete invoices",
      showSuccessToast: true,
      thenCallBack: () => {
        getAllData();
        clearSelectedRows();
        onClose();
      },
      errorCallback: (error) => {
        console.error("Error deleting invoices:", error);
      },
    });
  };
  return (
    <Dialog open={open} sx={{ "& .MuiDialog-paper": { borderRadius: "4px", minWidth: "600px" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2 }}>
        <Typography variant="h6">Bulk Actions</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ padding: "16px" }}>
        <Typography variant="subtitle2" sx={{ color: "rgba(0, 0, 0, 0.6)", marginBottom: 1 }}>
          {selectedRowIds.length} Invoices selected
        </Typography>
        {useCase === "previousPayments" && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                // if (useCase === "dailyPayments") {
                //   setOpenEditInvoiceModal(true);
                // } else
                if (useCase === "previousPayments") {
                  setOpenBulkOperationsModal(true);
                }
              }}
            >
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
              <ListItemText primary="Edit Invoices Details" secondary="Edit the details of all selected invoices" />
              <ListItemIcon sx={{ position: "relative", left: "30px" }}>
                <KeyboardArrowRightIcon />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        )}
        {useCase === "dailyPayments" && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => setOpenMoveInvoiceModal(true)}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Move Invoices" secondary="Move the selected invoices to previous payments" />
              <ListItemIcon sx={{ position: "relative", left: "30px" }}>
                <KeyboardArrowRightIcon />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenDeleteConfirmation(true)}>
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText primary="Delete Invoices" />
            <ListItemIcon sx={{ position: "relative", left: "30px" }}>
              <KeyboardArrowRightIcon />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      </List>
      {openEditInvoiceModal && (
        <EditInvoiceBulkModal open={openEditInvoiceModal} onClose={() => setOpenEditInvoiceModal(false)} />
      )}
      {openMoveInvoiceModal && (
        <ActionConfirmationModal
          open={openMoveInvoiceModal}
          onClose={() => setOpenMoveInvoiceModal(false)}
          onConfirm={handleMoveInvoices}
          title="Move Invoices"
          description="Are you sure you want to move the selected invoices?"
          buttonText="Move Invoices"
        />
      )}
      {openDeleteConfirmation && (
        <DeleteConfirmationModal
          open={openDeleteConfirmation}
          onClose={() => setOpenDeleteConfirmation(false)}
          onDelete={async () => {
            await handleDeleteInvoices();
          }}
          title="Are You Sure?"
          description="This action will permanently delete the invoice."
        />
      )}
      {openBulkOperationsModal && (
        <BulkOperationsModal
          open={openBulkOperationsModal}
          onClose={() => setOpenBulkOperationsModal(false)}
          selectedRowIds={selectedRowIds}
          clearSelectedRows={clearSelectedRows}
          useCase="admin"
          getAllData={getAllData}
          onCloseParent={onClose}
        />
      )}
    </Dialog>
  );
}
